/**
 * Flow Optimizer Drawer Tabs — labeled control row + full-width view tabs.
 * Exclusions and Formula use a split layout with a pinned live preview panel.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { Tabs } from '@/components/ui/layout/Tabs';

import FlowOptimizerViewToolbar from './FlowOptimizerViewToolbar';
import FlowOptimizerDrawerTabPanels from './FlowOptimizerDrawerTabPanels';
import { RefreshCapabilityRegistrar } from './refresh';
import { FlowOptimizerLivePreviewProvider } from './context/FlowOptimizerLivePreviewContext';
import DrawerTrackerShell from '@/features/flow-optimizer/drawer-shell/DrawerTrackerShell';
import { drawerProfile, type DrawerProfile } from '@/features/flow-optimizer/tracker';
import { usePreviewPanelPin } from './hooks';
import type { FlowOptimizerConfig } from '@trafficpulse/shared';
import type { FlowOptimizerTabKey } from './types/flow-optimizer.types';
import type { FlowOptimizerProvider } from './services/flow-optimizer-api';

export interface FlowOptimizerDrawerTabsProps {
  campaignId: string;
  draft: FlowOptimizerConfig;
  onChange: (next: FlowOptimizerConfig) => void;
  statusSlot: ReactNode;
  modeSlot: ReactNode;
  /** Library package picker (CUSTOM only) — composed by the drawer. */
  librarySlot?: ReactNode;
  provider?: FlowOptimizerProvider;
}

const customOnlyTabs = new Set<FlowOptimizerTabKey>([
  'preview',
  'rules',
  'queue',
  'tags',
  'calculation',
]);
const splitTabs = new Set<FlowOptimizerTabKey>(['rules', 'queue', 'calculation']);

function defaultTab(mode: FlowOptimizerConfig['mode']): FlowOptimizerTabKey {
  return mode === 'CUSTOM' ? 'preview' : 'standard';
}

/** A tab the tracker's profile does not offer (Queue / Tags on Tres Commas). */
function tabMissing(tab: FlowOptimizerTabKey, profile: DrawerProfile): boolean {
  return (tab === 'queue' && !profile.queue) || (tab === 'tags' && !profile.tagsTab);
}

function isBinomDrawerActive(tab: FlowOptimizerTabKey, isCustom: boolean): boolean {
  if (tab === 'campaign' || tab === 'advanced') return true;
  if (!isCustom && tab === 'standard') return true;
  if (isCustom && tab === 'preview') return true;
  return false;
}

const FlowOptimizerDrawerTabs: FC<FlowOptimizerDrawerTabsProps> = ({
  campaignId,
  draft,
  onChange,
  statusSlot,
  modeSlot,
  librarySlot,
  provider = 'voluum',
}) => {
  const [tab, setTab] = useState<FlowOptimizerTabKey>(() => defaultTab(draft.mode));
  const previewPin = usePreviewPanelPin();
  const isCustom = draft.mode === 'CUSTOM';
  const profile = useMemo(() => drawerProfile(provider), [provider]);
  const showPreviewControls = isCustom && splitTabs.has(tab);
  const splitActive = showPreviewControls && previewPin.pinned;

  useEffect(() => {
    if (!isCustom && customOnlyTabs.has(tab)) {
      setTab('standard');
      return;
    }
    if ((isCustom && tab === 'standard') || tabMissing(tab, profile)) {
      setTab('preview');
    }
  }, [isCustom, tab, profile]);

  const tabs = (
    <Tabs
      value={tab}
      onValueChange={(v) => setTab(v as FlowOptimizerTabKey)}
      className="flex min-h-0 flex-1 flex-col"
    >
      <FlowOptimizerViewToolbar
        isCustom={isCustom}
        profile={profile}
        showPreviewControls={showPreviewControls}
        previewPinned={previewPin.pinned}
        onTogglePreviewPin={previewPin.togglePinned}
        statusSlot={statusSlot}
        modeSlot={modeSlot}
        librarySlot={librarySlot}
      />
      <FlowOptimizerDrawerTabPanels
        tab={tab}
        campaignId={campaignId}
        draft={draft}
        onChange={onChange}
        provider={provider}
        profile={profile}
        isCustom={isCustom}
        splitActive={splitActive}
        onOpenPreviewTab={() => setTab('preview')}
        onUnpinPreview={() => previewPin.setPinned(false)}
      />
    </Tabs>
  );

  return (
    <DrawerTrackerShell
      provider={provider}
      campaignId={campaignId}
      draft={draft}
      binomDrawerActive={isBinomDrawerActive(tab, isCustom)}
    >
      <FlowOptimizerLivePreviewProvider
        campaignId={campaignId}
        provider={provider}
        draft={draft}
      >
        {/* Headless: publishes the weights/flow refetchers to the header's
            Refresh button, which lives OUTSIDE these providers. Sits here rather
            than in a tab so the button works on every tab. */}
        <RefreshCapabilityRegistrar />
        {tabs}
      </FlowOptimizerLivePreviewProvider>
    </DrawerTrackerShell>
  );
};

export default FlowOptimizerDrawerTabs;
